import axios from 'axios';
import { API, USDA_KEY, GROQ_KEY } from './config';
import { getToken, logout } from './auth';

// ── Interceptors ──────────────────────────────────────────────

axios.interceptors.request.use(config => {
  const url        = config.url || '';
  const isExternal = url.includes('groq.com')
    || url.includes('usda.gov')
    || url.includes('nal.usda.gov');
  if (!isExternal) {
    const token = getToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  res => res,
  err => {
    const url      = err.config?.url || '';
    const isOurAPI = url.includes('execute-api.amazonaws.com');
    if (err.response?.status === 401 && isOurAPI) {
      logout();
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────

export async function apiSignup(data) {
  const res = await axios.post(`${API}/auth/signup`, data);
  return res.data;
}

export async function apiLogin(data) {
  const res = await axios.post(`${API}/auth/login`, data);
  return res.data;
}

export async function apiGetMe() {
  const res = await axios.get(`${API}/auth/me`);
  return res.data;
}

export async function apiUpdateProfile(data) {
  const res = await axios.put(`${API}/auth/profile`, data);
  return res.data;
}

// ── Meals ─────────────────────────────────────────────────────

export async function fetchMeals(date) {
  const res = await axios.get(`${API}/meals?date=${date}`);
  return res.data;
}

export async function saveMeal(meal) {
  const res = await axios.post(`${API}/meals`, meal);
  return res.data;
}

export async function updateMeal(meal) {
  const res = await axios.put(`${API}/meals`, meal);
  return res.data;
}

export async function deleteMeal(timestamp) {
  const encoded = encodeURIComponent(timestamp);
  const res = await axios.delete(`${API}/meals?timestamp=${encoded}`);
  return res.data;
}

// ── Goals ─────────────────────────────────────────────────────

export async function fetchGoals() {
  const res = await axios.get(`${API}/goals`);
  return res.data;
}

export async function saveGoals(goals) {
  const res = await axios.post(`${API}/goals`, goals);
  return res.data;
}

// ── Water ─────────────────────────────────────────────────────

export async function fetchWater(date) {
  const res = await axios.get(`${API}/water?date=${date}`);
  return res.data;
}

export async function logWater(date, ml) {
  const res = await axios.post(`${API}/water`, { date, ml });
  return res.data;
}

// ── Notifications ─────────────────────────────────────────────

export async function fetchNotifications() {
  const res = await axios.get(`${API}/notifications`);
  return res.data;
}

export async function saveNotifications(prefs) {
  const res = await axios.post(`${API}/notifications`, prefs);
  return res.data;
}

// ── Subscription ──────────────────────────────────────────────

export async function fetchSubscriptionStatus() {
  const res = await axios.get(`${API}/subscription/status`);
  return res.data;
}

export async function createCheckoutSession(interval) {
  const res = await axios.post(
    `${API}/subscription/checkout`, { interval }
  );
  return res.data;
}

// ── USDA ──────────────────────────────────────────────────────

export async function searchUSDA(query) {
  const res = await axios.get(
    'https://api.nal.usda.gov/fdc/v1/foods/search',
    { params: { query, api_key: USDA_KEY, pageSize: 5 } }
  );
  return (res.data.foods || []).map(food => {
    const get = (name) => {
      const n = food.foodNutrients?.find(n => n.nutrientName === name);
      return Math.round(n?.value || 0);
    };
    return {
      foodName:   food.description,
      calories:   get('Energy'),
      protein:    get('Protein'),
      carbs:      get('Carbohydrate, by difference'),
      fat:        get('Total lipid (fat)'),
      grams:      100,
      dataSource: 'USDA',
    };
  });
}

// ── Groq AI — text ────────────────────────────────────────────

export async function estimateWithGroq(foodName) {
  const prompt = `You are a nutrition expert with deep knowledge of South Asian, Indian, and Punjabi cuisine. Estimate macronutrients for 100g of "${foodName}". Respond with ONLY a JSON object, no markdown, no backticks: {"calories":150,"protein":12,"carbs":8,"fat":7}`;

  const res = await axios.post(
    'https://api.groq.com/openai/v1/chat/completions',
    {
      model:       'llama-3.3-70b-versatile',
      messages:    [{ role: 'user', content: prompt }],
      temperature: 0.1,
      max_tokens:  150,
    },
    {
      headers: {
        'Authorization': `Bearer ${GROQ_KEY}`,
        'Content-Type':  'application/json',
      },
    }
  );

  const text   = res.data.choices[0].message.content.trim();
  const clean  = text.replace(/```json|```/g, '').trim();
  const parsed = JSON.parse(clean);
  return {
    foodName,
    calories:   Math.round(parsed.calories || 0),
    protein:    Math.round(parsed.protein  || 0),
    carbs:      Math.round(parsed.carbs    || 0),
    fat:        Math.round(parsed.fat      || 0),
    grams:      100,
    dataSource: 'AI_ESTIMATED',
  };
}

// ── Groq AI — unit estimation ─────────────────────────────────

export async function estimateMacrosForUnit(foodName, quantity, unit) {
  const prompt = `You are a nutrition expert with deep knowledge of South Asian, Indian, and Punjabi cuisine. Estimate macronutrients for ${quantity} ${unit} of "${foodName}". Also estimate the weight in grams. Respond with ONLY a JSON object, no markdown: {"calories":95,"protein":0.5,"carbs":25,"fat":0.3,"estimatedGrams":180}`;

  const res = await axios.post(
    'https://api.groq.com/openai/v1/chat/completions',
    {
      model:       'llama-3.3-70b-versatile',
      messages:    [{ role: 'user', content: prompt }],
      temperature: 0.1,
      max_tokens:  150,
    },
    {
      headers: {
        'Authorization': `Bearer ${GROQ_KEY}`,
        'Content-Type':  'application/json',
      },
    }
  );

  const text   = res.data.choices[0].message.content.trim();
  const clean  = text.replace(/```json|```/g, '').trim();
  const parsed = JSON.parse(clean);
  return {
    calories:       Math.round(parsed.calories       || 0),
    protein:        Math.round(parsed.protein        || 0),
    carbs:          Math.round(parsed.carbs          || 0),
    fat:            Math.round(parsed.fat            || 0),
    estimatedGrams: Math.round(parsed.estimatedGrams || 0),
  };
}

// ── Groq AI — photo logging ───────────────────────────────────

export async function estimateMacrosFromPhoto(
  base64Image, mimeType = 'image/jpeg'
) {
  const res = await axios.post(
    'https://api.groq.com/openai/v1/chat/completions',
    {
      model: 'meta-llama/llama-4-scout-17b-16e-instruct',
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'image_url',
              image_url: {
                url: `data:${mimeType};base64,${base64Image}`,
              },
            },
            {
              type: 'text',
              text: `You are a nutrition expert with deep knowledge of South Asian, Indian, and Punjabi cuisine. Look at this food image and estimate the macronutrients for the portion shown. Respond ONLY with a JSON object, no explanation, no markdown:
{"foodName":"Chicken Biryani","calories":450,"protein":28,"carbs":52,"fat":12,"grams":300}
where grams is your estimate of the total portion weight shown in the image.`,
            },
          ],
        },
      ],
      max_tokens:  200,
      temperature: 0.1,
    },
    {
      headers: {
        'Authorization': `Bearer ${GROQ_KEY}`,
        'Content-Type':  'application/json',
      },
    }
  );

  const text   = res.data.choices[0].message.content.trim();
  const clean  = text.replace(/```json|```/g, '').trim();
  const parsed = JSON.parse(clean);
  return {
    foodName:   parsed.foodName || 'Unknown food',
    calories:   Math.round(parsed.calories || 0),
    protein:    Math.round(parsed.protein  || 0),
    carbs:      Math.round(parsed.carbs    || 0),
    fat:        Math.round(parsed.fat      || 0),
    grams:      Math.round(parsed.grams    || 100),
    dataSource: 'AI_ESTIMATED',
  };
}

// ── Groq AI — weekly summary ──────────────────────────────────

export async function getAIWeeklySummary(weekData, goals) {
  const days = weekData.filter(d => d.calories > 0).length;
  const total = weekData.length || 1;

  const avg = weekData.reduce((acc, d) => ({
    calories: acc.calories + d.calories,
    protein:  acc.protein  + d.protein,
    carbs:    acc.carbs    + d.carbs,
    fat:      acc.fat      + d.fat,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  avg.calories = Math.round(avg.calories / total);
  avg.protein  = Math.round(avg.protein  / total);
  avg.carbs    = Math.round(avg.carbs    / total);
  avg.fat      = Math.round(avg.fat      / total);

  const best  = weekData.reduce((a, b) =>
    a.calories > b.calories ? a : b);
  const worst = weekData.reduce((a, b) =>
    a.calories < b.calories ? a : b);

  const prompt = `You are a friendly, encouraging nutrition coach with expertise in South Asian and Indian cuisine.
Analyse this user's 7-day diet data and give a short, personalised, actionable summary in 3-4 sentences.
Be specific, warm, and practical. Mention actual numbers. End with one concrete suggestion for next week.

User's data:
- Days logged: ${days} out of 7
- Average daily calories: ${avg.calories} kcal (goal: ${goals.dailyCalories} kcal)
- Average protein: ${avg.protein}g (goal: ${goals.dailyProtein}g)
- Average carbs: ${avg.carbs}g (goal: ${goals.dailyCarbs}g)
- Average fat: ${avg.fat}g (goal: ${goals.dailyFat}g)
- Best day: ${best.day} with ${best.calories} kcal
- Lowest day: ${worst.day} with ${worst.calories} kcal
- Consistency: logged meals ${days}/7 days

Respond with plain text only — no bullet points, no markdown, no headers. Just 3-4 warm conversational sentences.`;

  const res = await axios.post(
    'https://api.groq.com/openai/v1/chat/completions',
    {
      model:       'llama-3.3-70b-versatile',
      messages:    [{ role: 'user', content: prompt }],
      temperature: 0.7,
      max_tokens:  300,
    },
    {
      headers: {
        'Authorization': `Bearer ${GROQ_KEY}`,
        'Content-Type':  'application/json',
      },
    }
  );

  return res.data.choices[0].message.content.trim();
}